import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  createWinbackCheckoutSession,
  fetchWinbackOfferDetails,
  getAnalyticsEventContext,
  logAnalyticsEvent,
} from './Firebase';
import {
  WINBACK_CHECKOUT_STATES,
  WINBACK_STANDARD_YEARLY_PRICE,
  buildWinbackRedirectUrls,
  decodeWinbackTokenPayload,
  getInitialWinbackCheckoutState,
  getWinbackCheckoutParams,
  getWinbackOfferDetails,
  normalizeWinbackOfferDetails,
  resolveWinbackErrorState,
} from './checkoutState';

const FLUFFBALL_APP_STORE_URL = 'https://apps.apple.com/us/app/fluffball/id6761160687';
const LOCAL_PREVIEW_STATES = new Set([
  WINBACK_CHECKOUT_STATES.ALREADY_SUBSCRIBED,
  WINBACK_CHECKOUT_STATES.ERROR,
]);

const FALLBACK_LOCKED_PREVIEW = {
  kind: 'video',
  url: '/app/newborn.mp4',
  posterUrl: '/app/newborn.jpg',
};

const primaryCheckoutCtaClasses =
  'inline-flex min-h-[58px] w-full items-center justify-center gap-3 rounded-[18px] bg-gradient-to-r from-[#ec4899] to-[#8b5cf6] px-6 py-3 text-[17px] font-extrabold leading-none text-white shadow-[0_10px_24px_rgba(236,72,153,0.22)] transition duration-200 hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-70';

const mobileTitleStyle = {
  fontFamily: 'Charter, Georgia, "Times New Roman", serif',
};

const ButtonSpinner = () => (
  <span className="h-5 w-5 shrink-0 rounded-full border-2 border-white/40 border-t-white animate-spin" />
);

const getOfferTitle = (petName) => {
  const name = String(petName || '').trim();
  return name ? `${name} is waiting` : 'Your Fluffball is waiting';
};

const getDiscountBadgeLabel = (discountLabel) => {
  const cleaned = String(discountLabel || '').trim();
  if (/half\s+off/i.test(cleaned)) {
    return '50% off';
  }

  const dollarMatch = cleaned.match(/\$\d+(?:\.\d{2})?\s*off/i);
  if (dollarMatch) {
    return dollarMatch[0].replace(/\s+/g, ' ');
  }

  return 'Annual';
};

const getLocalPreviewState = (search) => {
  if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') {
    return '';
  }
  const host = String(window.location.hostname || '').toLowerCase();
  if (!['localhost', '127.0.0.1', '::1'].includes(host)) {
    return '';
  }
  const state = String(new URLSearchParams(search || '').get('preview_state') || '').trim();
  return LOCAL_PREVIEW_STATES.has(state) ? state : '';
};

const RevealMedia = ({ media, blur = true, poster = FALLBACK_LOCKED_PREVIEW.posterUrl }) => {
  const preview = media?.url ? media : FALLBACK_LOCKED_PREVIEW;
  const isVideo =
    preview.kind === 'video' || /\.(mp4|webm|mov)(?:$|\?)/i.test(String(preview.url || ''));
  const mediaClass = `absolute inset-0 h-full w-full object-cover ${blur ? 'scale-[1.06] blur-[9px]' : ''}`;

  if (isVideo) {
    return (
      <video
        aria-label={blur ? 'Blurred preview of the Fluffball reveal' : 'Fluffball reveal video'}
        className={mediaClass}
        autoPlay
        muted
        loop
        playsInline
        poster={preview.posterUrl || poster}
      >
        <source src={preview.url || FALLBACK_LOCKED_PREVIEW.url} />
      </video>
    );
  }

  return (
    <img
      src={preview.url}
      alt={blur ? 'A blurred preview of your pet reveal' : 'Your pet reveal'}
      className={mediaClass}
      draggable={false}
    />
  );
};

const MobileRevealShell = ({
  title,
  media,
  blurMedia = true,
  children,
}) => (
  <main className="min-h-screen min-h-[100dvh] bg-white text-[#1e293b]">
    <div className="mx-auto flex min-h-screen min-h-[100dvh] max-w-[430px] flex-col px-4 pb-[calc(env(safe-area-inset-bottom)+16px)] pt-[calc(env(safe-area-inset-top)+38px)]">
      <h1
        className="shrink-0 px-2 text-center text-[clamp(28px,7.4vw,34px)] font-bold leading-[1.08] tracking-normal text-[#1e293b]"
        style={mobileTitleStyle}
      >
        {title}
      </h1>

      <section className="relative mt-5 min-h-0 flex-1 overflow-hidden rounded-[28px] bg-[#0a0a0a] shadow-[0_18px_44px_rgba(15,23,42,0.14)]">
        <RevealMedia media={media} blur={blurMedia} />
        {blurMedia ? <div className="absolute inset-0 bg-white/8" /> : null}
      </section>

      <div className="shrink-0 pt-4">{children}</div>
    </div>
  </main>
);

const OfferPlanPanel = ({
  canceled,
  ctaLabel,
  discountLabel,
  firstYearPrice,
  onClaimClick,
  submitting,
  standardYearlyPrice,
}) => {
  const discountBadgeLabel = getDiscountBadgeLabel(discountLabel);

  return (
    <section className="rounded-[26px] border border-slate-200/80 bg-white p-3.5 shadow-[0_18px_42px_rgba(15,23,42,0.10)]">
      {canceled ? (
        <p className="mb-3 rounded-[16px] bg-slate-50 px-3 py-2 text-center text-xs font-semibold leading-5 text-slate-500">
          Checkout was canceled. No charge was made.
        </p>
      ) : null}

      <div className="relative rounded-[22px] border-2 border-[#ec4899] bg-[#fff7fb] px-4 py-3.5">
        <span className="absolute right-3 top-0 -translate-y-1/2 rounded-full bg-[#ec4899] px-3 py-1 text-[11px] font-extrabold uppercase tracking-normal text-white shadow-[0_6px_14px_rgba(236,72,153,0.24)]">
          {discountBadgeLabel}
        </span>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-[16px] font-extrabold leading-5 text-slate-900">
              Fluffball Pro Annual
            </p>
            <p className="mt-1 text-xs font-semibold leading-4 text-slate-500">
              First year
            </p>
          </div>
          <div className="shrink-0 text-right">
            <p className="text-[24px] font-black leading-none text-slate-950">
              {firstYearPrice || standardYearlyPrice}
            </p>
            {firstYearPrice ? (
              <p className="mt-1 text-xs font-semibold leading-4 text-slate-500">
                <span className="line-through">{standardYearlyPrice}</span>
              </p>
            ) : (
              <p className="mt-1 text-xs font-semibold leading-4 text-slate-500">per year</p>
            )}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={onClaimClick}
        disabled={submitting}
        aria-busy={submitting}
        className={`${primaryCheckoutCtaClasses} mt-3`}
      >
        {submitting ? (
          <>
            <ButtonSpinner />
            Opening Stripe...
          </>
        ) : (
          ctaLabel
        )}
      </button>

      <p className="mx-auto mt-3 max-w-[340px] text-center text-[11px] font-medium leading-[17px] text-slate-400">
        Includes one year of Fluffball Pro. Renews at {standardYearlyPrice}/year until canceled.
        Cancel anytime in Fluffball.
      </p>
      <p className="mt-1 text-center text-[11px] font-semibold leading-4 text-slate-500">
        <Link to="/terms" className="font-semibold text-slate-500 underline underline-offset-2">
          Terms
        </Link>
        <span aria-hidden="true"> · </span>
        <Link to="/privacy" className="font-semibold text-slate-500 underline underline-offset-2">
          Privacy
        </Link>
      </p>
    </section>
  );
};

const StatusActionLink = ({ href, onClick, children }) => (
  <a href={href} onClick={onClick} className={primaryCheckoutCtaClasses}>
    {children}
  </a>
);

const StatusMessageScreen = ({ title, body, action }) => (
  <main className="min-h-screen min-h-[100dvh] bg-white text-[#1e293b]">
    <div className="mx-auto flex min-h-screen min-h-[100dvh] max-w-[430px] items-center px-4 py-[calc(env(safe-area-inset-top)+24px)]">
      <section className="w-full rounded-[28px] border border-slate-200/80 bg-white p-5 text-center shadow-[0_18px_42px_rgba(15,23,42,0.10)]">
        <h1
          className="mx-auto max-w-[340px] text-[clamp(28px,7.4vw,34px)] font-bold leading-[1.08] tracking-normal text-[#1e293b]"
          style={mobileTitleStyle}
        >
          {title}
        </h1>
        {body ? (
          <p className="mx-auto mt-3 max-w-[320px] text-[15px] font-medium leading-6 text-slate-500">
            {body}
          </p>
        ) : null}
        <div className="mt-5">{action}</div>
      </section>
    </div>
  </main>
);

const LoadingOfferScreen = () => (
  <main className="min-h-screen min-h-[100dvh] bg-white text-[#1e293b]">
    <div className="mx-auto flex min-h-screen min-h-[100dvh] max-w-[430px] items-center justify-center px-4 py-[calc(env(safe-area-inset-top)+24px)]">
      <div
        aria-label="Loading checkout offer"
        className="h-9 w-9 rounded-full border-4 border-slate-200 border-t-[#ec4899] animate-spin"
      />
    </div>
  </main>
);

const CheckoutPage = () => {
  const location = useLocation();
  const { token, canceled } = useMemo(
    () => getWinbackCheckoutParams(location.search),
    [location.search]
  );
  const payload = useMemo(() => decodeWinbackTokenPayload(token), [token]);
  const clientOffer = getWinbackOfferDetails(payload?.tier);
  const [checkoutState, setCheckoutState] = useState(() => {
    const params = getWinbackCheckoutParams(location.search);
    const localPreviewState = getLocalPreviewState(location.search);
    if (localPreviewState) {
      return localPreviewState;
    }
    return getInitialWinbackCheckoutState({
      token: params.token,
      payload: decodeWinbackTokenPayload(params.token),
    });
  });
  const [offerDetails, setOfferDetails] = useState(null);
  const [offerLoadComplete, setOfferLoadComplete] = useState(() => checkoutState !== WINBACK_CHECKOUT_STATES.OFFER);
  const [submitting, setSubmitting] = useState(false);
  const viewLoggedRef = useRef(false);

  const trackCheckoutEvent = useCallback(
    (eventName, properties = {}) => {
      logAnalyticsEvent({
        analyticsContext: getAnalyticsEventContext(),
        eventName,
        properties: {
          screen: 'winback_checkout',
          offer_tier: payload?.tier || 0,
          ...properties,
        },
      });
    },
    [payload]
  );

  useEffect(() => {
    if (checkoutState !== WINBACK_CHECKOUT_STATES.OFFER || !token) {
      return undefined;
    }
    let cancelled = false;
    setOfferLoadComplete(false);
    fetchWinbackOfferDetails({ token })
      .then((response) => {
        if (cancelled) {
          return;
        }
        setOfferDetails(normalizeWinbackOfferDetails(response));
        setOfferLoadComplete(true);
        trackCheckoutEvent('fluffball-winback-offer-loaded', { render: 'rich' });
      })
      .catch((offerError) => {
        if (cancelled) {
          return;
        }
        const code = String(offerError?.code || '').trim().toLowerCase();
        if (code === 'invalid_token' || code === 'already_subscribed') {
          setCheckoutState(resolveWinbackErrorState(offerError));
          setOfferLoadComplete(true);
          return;
        }
        setOfferLoadComplete(true);
        trackCheckoutEvent('fluffball-winback-offer-loaded', { render: 'fallback' });
      });
    return () => {
      cancelled = true;
    };
  }, [checkoutState, token, trackCheckoutEvent]);

  const startCheckout = useCallback(async () => {
    setSubmitting(true);
    try {
      const { successUrl, cancelUrl } = buildWinbackRedirectUrls(window.location.origin, token);
      const { url } = await createWinbackCheckoutSession({ token, successUrl, cancelUrl });
      trackCheckoutEvent('fluffball-winback-checkout-redirected');
      window.location.assign(url);
    } catch (checkoutError) {
      console.error('Failed to start win-back checkout:', checkoutError);
      const nextState = resolveWinbackErrorState(checkoutError);
      trackCheckoutEvent('fluffball-winback-checkout-blocked', { state: nextState });
      setCheckoutState(nextState);
      setSubmitting(false);
    }
  }, [token, trackCheckoutEvent]);

  useEffect(() => {
    if (viewLoggedRef.current) {
      return;
    }
    viewLoggedRef.current = true;
    trackCheckoutEvent('fluffball-winback-checkout-viewed', {
      state: checkoutState,
      has_token: Boolean(token),
      token_parsed: Boolean(payload),
      returned_from_cancel: canceled,
    });
  }, [canceled, checkoutState, payload, token, trackCheckoutEvent]);

  const handleClaimClick = useCallback(() => {
    if (submitting) {
      return;
    }
    trackCheckoutEvent('fluffball-winback-checkout-cta-clicked', { state: checkoutState });
    startCheckout();
  }, [checkoutState, startCheckout, submitting, trackCheckoutEvent]);

  const handleOpenAppClick = useCallback(() => {
    trackCheckoutEvent('fluffball-winback-checkout-open-app-clicked', { state: checkoutState });
  }, [checkoutState, trackCheckoutEvent]);

  if (checkoutState === WINBACK_CHECKOUT_STATES.EXPIRED) {
    return (
      <StatusMessageScreen
        title="This offer expired"
        body="This checkout link is no longer available. No payment was attempted."
        action={
          <StatusActionLink href={FLUFFBALL_APP_STORE_URL} onClick={handleOpenAppClick}>
            Open Fluffball
          </StatusActionLink>
        }
      />
    );
  }

  if (checkoutState === WINBACK_CHECKOUT_STATES.ALREADY_SUBSCRIBED) {
    return (
      <StatusMessageScreen
        title="You already have Pro"
        body="This offer is no longer needed because Fluffball Pro access is active."
        action={
          <StatusActionLink href={FLUFFBALL_APP_STORE_URL} onClick={handleOpenAppClick}>
            Open Fluffball
          </StatusActionLink>
        }
      />
    );
  }

  if (checkoutState === WINBACK_CHECKOUT_STATES.ERROR) {
    return (
      <StatusMessageScreen
        title="Checkout did not open"
        body="Something went wrong starting checkout. No charge was made."
        action={
          <button
            type="button"
            onClick={handleClaimClick}
            disabled={submitting}
            aria-busy={submitting}
            className={primaryCheckoutCtaClasses}
          >
            {submitting ? (
              <>
                <ButtonSpinner />
                Opening Stripe...
              </>
            ) : (
              'Try again'
            )}
          </button>
        }
      />
    );
  }

  const petName = offerDetails?.petName || '';
  const firstYearPrice = offerDetails?.firstYearPrice || clientOffer?.firstYearPrice || '';
  const discountLabel = offerDetails?.discountLabel || clientOffer?.discountLabel || '';
  const standardYearlyPrice = offerDetails?.standardYearlyPrice || WINBACK_STANDARD_YEARLY_PRICE;
  const ctaLabel = 'Continue to checkout';
  const title = getOfferTitle(petName);

  if (!offerLoadComplete) {
    return <LoadingOfferScreen />;
  }

  return (
    <MobileRevealShell
      title={title}
      media={offerDetails?.media}
      blurMedia
    >
      <OfferPlanPanel
        canceled={canceled}
        ctaLabel={ctaLabel}
        discountLabel={discountLabel}
        firstYearPrice={firstYearPrice}
        onClaimClick={handleClaimClick}
        submitting={submitting}
        standardYearlyPrice={standardYearlyPrice}
      />
    </MobileRevealShell>
  );
};

export default CheckoutPage;
